import styles from "../defineDefaultValues/defineDefaultValues.module.scss";
import { ModalWindow } from "../../../shared/components/modalWindow/modalWindow";
import { useTranslation } from "react-i18next";
import {
  array,
  number,
  object
} from "yup";
import {
  useFieldArray,
  useForm
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  useCallback,
  useEffect,
  useMemo, useState
} from "react";
import {
  Variable,
  VariableValue
} from "../../../types/variableType";
import {
  Dropdown,
  DropdownItem
} from "../../../shared/components/dropdown/dropdown";
import { MessageWindow } from "../../../shared/components/messageWindow/messageWindow";
import { useDeleteVariableValueMutation } from "../../../api/variableService";
import {
  setSelectedEnvironment,
  setShowSpinner
} from "../../../store/slices/mainSlice";
import {
  useDispatch,
  useSelector
} from "react-redux";
import {
  AppDispatch,
  RootState
} from "../../../store";
import { useGetAllEnvironmentsQuery } from "../../../api/environmentService";
import { findProjectSettings } from "../../../store/selectors";

type DeleteVariableValueModalProps = {
  deleteVariableValue: VariableValue;
  editVariable: Variable;
  onClose: (isSave: boolean, newVariableId?: number) => void;
}

export const DeleteVariableValueModal = ({ onClose, deleteVariableValue, editVariable }: DeleteVariableValueModalProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();

  const selectedEnvironment = useSelector((state: RootState) => findProjectSettings(state).selectedEnvironment);

  const [isRefetching, setIsRefetching] = useState(false);

  const { data: environments, refetch: refetchEnvironments } = useGetAllEnvironmentsQuery("");

  const [deleteVariableValueMutation, { isSuccess: isSuccessDeleteVariableValue, isLoading: isLoadingDelete }] = useDeleteVariableValueMutation();

  const schema = object({
    environmentValues: array().required().of(
      object().shape({
        environmentId: number().required(t("common.notValid")),
        variableValueId: number().required(t("common.notValid")).test(
          'is-not-zero',
          t("common.notValid"),
          value => value !== 0
        )
      })
    )
  });

  const neededEnvironments = useMemo(() => {
    return environments?.filter(environment =>
      environment.defaultVariableValues.some(variable => variable.variableValueId === deleteVariableValue.id)
    ) || [];
  }, [environments, deleteVariableValue]);

  const isSelectedEnvironment = useMemo(() => {
    return neededEnvironments.some(item => item.id === selectedEnvironment.id);
  }, [selectedEnvironment, neededEnvironments]);

  useEffect(() => {
    dispatch(setShowSpinner(isLoadingDelete));
  }, [isLoadingDelete, dispatch]);

  useEffect(() => {
    if (isSuccessDeleteVariableValue) {
      if (isSelectedEnvironment) {
        refetchEnvironments().unwrap().then(() => {
          setIsRefetching(true);
        });
      } else {
        refetchEnvironments();
        onClose(true);
      }
    }
  }, [isSuccessDeleteVariableValue, isSelectedEnvironment, refetchEnvironments, setIsRefetching]);

  useEffect(() => {
    if (isRefetching && environments) {
      const environment = environments.find(environment => environment.id === selectedEnvironment.id);
      const newDefaultValue = environment?.defaultVariableValues.find(item => item.variableId === editVariable.id);
      if (!!environment && !!newDefaultValue) {
        dispatch(setSelectedEnvironment(environment));
        onClose(true,  newDefaultValue.variableValueId);
      } else {
        console.error("Error set default variables");
        onClose(true);
      }
    }
  }, [isRefetching, environments, selectedEnvironment]);

  const defaultValues = useMemo(() => {
    if (neededEnvironments) {
      return {
        environmentValues: neededEnvironments.map(environment => ({
          environmentId: environment.id,
          variableValueId: 0
        }))
      };
    }
  }, [neededEnvironments]);

  const { control, handleSubmit, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultValues,
    mode: "onChange"
  });
  const { fields: environmentValuesFields } = useFieldArray({ control, name: "environmentValues" });

  const onClickDelete = useCallback(() => {
    handleSubmit((result) => {
      if (environments) {
        deleteVariableValueMutation({
          variableValueId: deleteVariableValue.id,
          environmentValues: environments.map(environment => {
            let variableValueId = result.environmentValues.find(item => item.environmentId === environment.id)?.variableValueId;
            if (!variableValueId) {
              variableValueId = environment.defaultVariableValues.find(env => env.variableId === editVariable.id)?.variableValueId
            }
            if (!variableValueId) {
              console.log("Error in delete");
            }
            return {
              environmentId: environment.id,
              variableValueId: variableValueId || 0
            }
          })
        });
      } else {
        console.log("Error Delete");
      }
    })();
  }, [handleSubmit, deleteVariableValueMutation, editVariable, environments]);

  const getEnvironmentName = useCallback((environmentId: number) => {
    return neededEnvironments?.find(item => item.id === environmentId)?.name;
  }, [neededEnvironments]);

  const dropdownItems: Array<DropdownItem> = useMemo(() => {
    return editVariable.values.filter(value => value.id !== deleteVariableValue.id).map(value => ({
      id: value.id,
      name: value.value,
      title: value.value
    }));
  }, [editVariable]);

  const closeDeleteMessageWindow = useCallback((isConfirm: boolean) => {
    if (isConfirm) {
      if (!!environments) {
        deleteVariableValueMutation({
          variableValueId: deleteVariableValue.id,
          environmentValues: environments.map(environment => {
            const variableValueId = environment.defaultVariableValues.find(env => env.variableId === editVariable.id)?.variableValueId
            if (!variableValueId) {
              console.log("Error in delete");
            }
            return {
              environmentId: environment.id,
              variableValueId: variableValueId || 0
            }
          })
        });
      } else {
        console.error("Error delete");
      }

    } else {
      onClose(false);
    }
  }, [deleteVariableValueMutation, deleteVariableValue, editVariable, environments]);

  return neededEnvironments.length ? (
    <ModalWindow
      title={t("variableManager.defineDefaultValues")}
      onClose={() => onClose(false)}
      width={380}
      children={
        <div className={styles.container}>
          <div className={styles.header}>
            {t("variableManager.variable")}:
            <span className={styles.variableName}>
              {editVariable.name}
            </span>
          </div>
          <div className={styles.values}>
            <div className={styles.title}>
              {t("testCaseTemplate.environments")}
            </div>
            { environmentValuesFields.map((environmentValueField, index) => (
              <div className={styles.item}
                   key={environmentValueField.id}>
                <div className={styles.label}>
                  {getEnvironmentName(environmentValueField.environmentId)}
                </div>
                <div className={styles.inputContainer}>
                  <div className={styles.dropdown}>
                    <Dropdown control={control}
                              showCleanField
                              items={dropdownItems}
                              error={errors.environmentValues?.[index]?.variableValueId ? errors.environmentValues[index]?.variableValueId?.message : ""}
                              name={`environmentValues.${index}.variableValueId`}/>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className={styles.buttons}>
            <button className={styles.cancel}
                    onClick={() => onClose(false)}>{t("common.cancel")}</button>
            <button className={styles.save}
                    onClick={onClickDelete}>{t("common.delete")}</button>
          </div>
        </div>
      }
    />
  ) : (<MessageWindow onClose={closeDeleteMessageWindow}
                      title={t("variableManager.deleteValue")}
                      description={t("variableManager.deleteDescription")}
                      primaryText={t("common.ok")} secondaryText={t("common.cancel")}/>)
}